import { QR_LIST, QR_GET, QR_STATS } from "@/apollo/queries/qr";
import { QR_MOVE, QR_ARCHIVE, QR_SAVE, QR_STATUS } from "@/apollo/mutations/qr";
import { exMut, exQue } from "@/utils/gql";

// Página actual de la lista de códigos QR de la empresa. Cada elemento trae lo justo para la
// tabla (QR_LIST_FIELDS: sin el diseño). `version` hay que devolverla al mover o archivar, porque
// el servidor rechaza la operación si el QR cambió mientras tanto.
//
// El estado es una función por la misma razón que en Carpetas.js.
export const CodigosState = () => ({
  codigos: [],
  codigosTotal: 0,
  codigosCargando: false,
});

export const CodigosMutations = {
  setCodigos(state, { items, total }) {
    state.codigos = items;
    state.codigosTotal = total;
  },
  setCodigosCargando(state, cargando) {
    state.codigosCargando = cargando;
  },
};

// Número de la última consulta. Al escribir en el buscador salen varias seguidas y pueden
// volver desordenadas: solo la última cuenta.
let consultaVigente = 0;

export const CodigosActions = {
  // Carga una página con { search, offset, limit, folderId, archive }. Devuelve la página, o
  // false si una consulta más nueva la reemplazó. Si falla avisa con un toast y vuelve a lanzar
  // el error, para que la pantalla decida qué hacer.
  async getCodigos({ commit }, filtros) {
    const consulta = ++consultaVigente;
    commit("setCodigosCargando", true);
    try {
      const pagina = await exQue(
        QR_LIST,
        filtros,
        true,
        "",
        "No se pudieron cargar los códigos QR",
      );
      if (consulta !== consultaVigente) return false;
      commit("setCodigos", { items: pagina.items, total: pagina.total });
      return pagina;
    } catch (error) {
      if (consulta === consultaVigente) throw error;
      return false;
    } finally {
      if (consulta === consultaVigente) commit("setCodigosCargando", false);
    }
  },

  // Trae un QR completo, con su logo, para abrir el editor o el detalle. La lista no sirve
  // para eso: no pide el diseño. Si falla avisa con un toast y vuelve a lanzar el error.
  async getCodigo(_, id) {
    return exQue(QR_GET, { id }, true, "", "No se pudo cargar el código QR");
  },

  // Crea o actualiza un QR. `input` es el QrAdminSaveInput del backend: sin `id` crea un
  // borrador y con `id` y `version` actualiza, que es como el servidor detecta que otra
  // persona lo cambió mientras tanto. Devuelve el QR guardado, o null si lo rechazó.
  async guardarCodigo(_, { input, esEdicion }) {
    return exMut(
      QR_SAVE,
      { input },
      esEdicion
        ? "Código QR actualizado correctamente"
        : "Borrador creado. Publícalo desde su detalle cuando esté listo.",
      esEdicion ? "No se pudo guardar el código QR" : "No se pudo crear el código QR",
    );
  },

  // Mueve un QR a una carpeta ("" = sin carpeta). Devuelve el QR o null si el servidor lo
  // rechazó: exMut ya avisó con su mensaje.
  async moverCodigo(_, { id, version, folderId }) {
    return exMut(
      QR_MOVE,
      { id, version, folderId },
      "QR movido. Conserva su enlace y sus lecturas.",
      "No se pudo mover el QR",
    );
  },

  // Archiva o restaura un QR. Archivar no cambia si está activo o pausado ni detiene su enlace.
  async archivarCodigo(_, { id, version, archivar }) {
    return exMut(
      QR_ARCHIVE,
      { id, version, archived: archivar },
      archivar
        ? "QR archivado. Conserva su enlace y sus lecturas."
        : "QR restaurado al listado actual.",
      archivar ? "No se pudo archivar el QR" : "No se pudo restaurar el QR",
    );
  },

  // Publica, pausa o reactiva un QR. El backend solo acepta "active" y "paused", y no deja
  // pausar un borrador: publicar es pasar de borrador a activo, y ahí nace su enlace.
  async cambiarEstadoCodigo(_, { id, version, estado, publicando }) {
    const exito = {
      publicar: "QR publicado. Ya puedes descargarlo y medir sus lecturas.",
      active: "QR reactivado. Vuelve a llevar a su destino.",
      paused: "QR pausado. Puedes reactivarlo sin cambiar el archivo.",
    };
    return exMut(
      QR_STATUS,
      { id, version, status: estado },
      publicando ? exito.publicar : exito[estado],
      "No se pudo cambiar el estado del QR",
    );
  },

  // Lecturas de un QR en un rango de días (AAAA-MM-DD). El backend agrupa por día UTC y
  // admite hasta 366 días.
  async getEstadisticas(_, { id, desde, hasta }) {
    return exQue(
      QR_STATS,
      { id, from: desde, to: hasta },
      true,
      "",
      "No se pudieron cargar las lecturas del QR",
    );
  },
};
